import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/feature/SEOHead';
import HeroBackground from '../../components/feature/HeroBackground';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://example.com';

const resourcesSchema = {
  '@context': 'https://schema.org',
  '@type': 'CollectionPage',
  '@id': `${SITE_URL}/resources`,
  url: `${SITE_URL}/resources`,
  name: 'Resources – Industry Directories & Sourcing Guides | True Passage',
  description: 'Access comprehensive industry directories and sourcing guides for industrial manufacturers, suppliers, and procurement resources.',
  breadcrumb: {
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
      { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
    ],
  },
};

const ResourcesPage = () => {
  const directories = [
    {
      title: 'PU Belt Manufacturers Directory',
      description: 'Global and India-based polyurethane timing belt manufacturers and suppliers',
      link: '/pu-belt-manufacturers-directory',
      icon: 'ri-settings-3-line',
      categories: ['Global Manufacturers', 'India Manufacturers', 'Export Markets'],
    },
    {
      title: 'India Supplier Directory',
      description: 'Best websites and platforms to find verified manufacturers and suppliers across India',
      link: '/india-supplier-directory',
      icon: 'ri-global-line',
      categories: ['Industrial Suppliers', 'Machining Vendors', 'OEM Manufacturers'],
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Resources – Industry Directories & Sourcing Guides | True Passage"
        description="Access comprehensive industry directories and sourcing guides for industrial manufacturers, suppliers, and procurement resources."
        keywords="industrial directories, manufacturer directories, sourcing guides, supplier directories, procurement resources"
        canonical="/resources"
        schema={resourcesSchema}
      />
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-gradient-to-br from-navy-primary via-navy-secondary to-navy-primary overflow-hidden">
        <HeroBackground />
        <div className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            className="text-center max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
              Industry <span className="text-gold-accent">Resources</span>
            </h1>
            <p className="text-xl text-white/70 leading-relaxed">
              Comprehensive directories and sourcing guides for global industrial procurement
            </p>
          </motion.div>
        </div>
      </section>

      {/* Directories Section */}
      <section className="py-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl font-bold text-navy-primary mb-4">
              Manufacturer Directories
            </h2>
            <p className="text-lg text-navy-primary/70 max-w-2xl mx-auto">
              Curated lists of verified manufacturers and suppliers across key industrial categories
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {directories.map((directory, index) => (
              <motion.div
                key={directory.link}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
              >
                <Link
                  to={directory.link}
                  className="block bg-white border-2 border-navy-primary/10 rounded-2xl p-8 hover:border-gold-accent hover:shadow-xl transition-all group h-full"
                >
                  <div className="w-16 h-16 bg-gold-accent/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-gold-accent/20 transition-colors">
                    <i className={`${directory.icon} text-3xl text-gold-accent`}></i>
                  </div>

                  <h3 className="text-2xl font-bold text-navy-primary mb-3 group-hover:text-gold-accent transition-colors">
                    {directory.title}
                  </h3>

                  <p className="text-navy-primary/70 mb-6 leading-relaxed">
                    {directory.description}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-6">
                    {directory.categories.map((category) => (
                      <span
                        key={category}
                        className="px-3 py-1 bg-navy-primary/5 text-navy-primary text-xs rounded-full"
                      >
                        {category}
                      </span>
                    ))}
                  </div>

                  <div className="flex items-center gap-2 text-gold-accent font-semibold group-hover:gap-3 transition-all">
                    View Directory
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-neutral-light">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <motion.div
            className="bg-gradient-to-br from-navy-primary to-navy-secondary rounded-3xl p-12 lg:p-16 text-center"
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-4xl font-bold text-white mb-5">
              Need Help Finding the Right Supplier?
            </h2>
            <p className="text-lg text-white/70 mb-8 max-w-2xl mx-auto">
              True Passage can help you source from verified manufacturers and manage your global procurement
            </p>
            <Link
              to="/request-quote"
              className="inline-flex items-center gap-2 px-8 py-4 bg-gold-accent text-navy-primary rounded-full font-semibold text-base hover:scale-105 transition-transform shadow-lg hover:shadow-xl whitespace-nowrap"
            >
              Request Quote
              <i className="ri-arrow-right-line"></i>
            </Link>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ResourcesPage;